import * as THREE from 'three/webgpu';
import type { PhysicsWorld, StaticMesh } from '../physics/PhysicsWorld';
import { perpendicularBasis } from '../universe/Tunnel';
import { groundAlongAxis, mouthAxisDistance, type TerrainMouth } from './TerrainMouth';
import type { TerrainQuadtree, TNode } from './TerrainQuadtree';
import { type RootCoord, SphereShape } from './TerrainShape';
import { TILE_RES } from './TileGenerator';

const _pl = new THREE.Vector3();
const _e1 = new THREE.Vector3();
const _e2 = new THREE.Vector3();
const _p = new THREE.Vector3();
const _q = new THREE.Vector3();
const _n = new THREE.Vector3();
const _rc: RootCoord = { root: 0, u: 0, v: 0 };
const _local = new THREE.Vector3();
/** Collision quads this close to a mouth's entry cylinder are dropped; the collar takes over. */
const HOLE_MARGIN = 2.5;
const COLLAR_RINGS = [0, 0.75, 1.5, 2.5, 3.5, 5];
const COLLAR_SEGMENTS = 96;
/**
 * How far past its edge (a share of its width) the player may stand before the tile under
 * them becomes the new centre. Standing on an edge, the slightest movement flipped the centre
 * to the next tile and back, and each flip rebuilt the whole mesh.
 */
const CENTER_HYSTERESIS = 0.1;

/**
 * Collision for terrain around the player, built from the CPU copies of the
 * finest tiles (the same heights the GPU renders). The 3×3 neighbourhood of
 * tiles is merged into one trimesh so internal-edge fixing works across tile
 * seams; it is rebuilt when the player enters a new tile or more data arrives.
 */
export class TerrainCollider {
  private mesh: StaticMesh | null = null;
  private centerKey = -1;
  private centerNode: TNode | null = null;
  private builtCount = 0;
  private wanted: TNode[] = [];
  /** The neighbourhood's list, reused (wanted is this list after an update). */
  private readonly hood: TNode[] = [];
  readonly level: number;
  /** The sphere's radius, for cave-mouth collars (mouths only exist in spheres). */
  private readonly radius: number;

  constructor(
    private readonly physics: PhysicsWorld,
    private readonly tree: TerrainQuadtree,
    private readonly hollowCenter: THREE.Vector3,
    /** Cave mouths: their holes are cut out, and an exact collar rings each one. */
    private readonly mouths: TerrainMouth[] = [],
  ) {
    this.level = tree.maxLevel;
    this.radius = tree.shape instanceof SphereShape ? tree.shape.radius : 0;
    for (const m of mouths) this.buildCollar(m);
  }

  /**
   * Collision rings where the mouth's entry cylinder meets the ground (the plateau, a
   * sphere of radius R − height, with its lip), so there is no gap between the jagged
   * hole and the tunnel.
   */
  private buildCollar(m: TerrainMouth): void {
    const plateauR = this.radius - m.height;
    const nA = new THREE.Vector3();
    const nB = new THREE.Vector3();
    perpendicularBasis(m.axis, nA, nB);
    const rings = COLLAR_RINGS.length;
    const cols = COLLAR_SEGMENTS;
    const verts = new Float32Array(rings * cols * 3);
    const off = new THREE.Vector3();
    const o = new THREE.Vector3();
    for (let k = 0; k < rings; k++) {
      const rho = m.radius + COLLAR_RINGS[k];
      for (let j = 0; j < cols; j++) {
        const phi = (j / cols) * Math.PI * 2;
        off
          .copy(nA)
          .multiplyScalar(rho * Math.cos(phi))
          .addScaledVector(nB, rho * Math.sin(phi));
        // Where the line parallel to the axis through point + off meets the ground.
        const t = groundAlongAxis(m, plateauR, o.copy(m.point).add(off));
        const i = (k * cols + j) * 3;
        verts[i] = off.x + m.axis.x * t;
        verts[i + 1] = off.y + m.axis.y * t;
        verts[i + 2] = off.z + m.axis.z * t;
      }
    }
    const idx: number[] = [];
    for (let k = 0; k < rings - 1; k++) {
      for (let j = 0; j < cols; j++) {
        const a = k * cols + j;
        const b = (k + 1) * cols + j;
        const c = k * cols + ((j + 1) % cols);
        const d = (k + 1) * cols + ((j + 1) % cols);
        idx.push(a, b, c, b, d, c);
      }
    }
    this.physics.addStaticMesh(this.hollowCenter.clone().add(m.point), verts, new Uint32Array(idx));
  }

  /** The node (at the collision level) under a hollow-local point. Leaves _rc at the point. */
  private nodeUnder(local: THREE.Vector3): TNode {
    const shape = this.tree.shape;
    shape.locate(local, _rc);
    const n = 2 ** this.level;
    const x = Math.min(n - 1, Math.max(0, Math.floor(((_rc.u + 1) / 2) * n))) | 0;
    const y = Math.min(n - 1, Math.max(0, Math.floor(((_rc.v + 1) / 2) * n))) | 0;
    return this.tree.getNode(_rc.root, this.level, x, y);
  }

  /** Whether _rc lies on `node`, or no further than CENTER_HYSTERESIS of its width past its edge. */
  private holds(node: TNode): boolean {
    if (_rc.root !== node.face) return false;
    const size = 2 / 2 ** node.level;
    const m = size * CENTER_HYSTERESIS;
    const u0 = -1 + node.x * size;
    const v0 = -1 + node.y * size;
    return _rc.u >= u0 - m && _rc.u <= u0 + size + m && _rc.v >= v0 - m && _rc.v <= v0 + size + m;
  }

  /** Nodes (at the collision level) around a hollow-local point: one step each way along the surface. */
  neighbourhood(local: THREE.Vector3): TNode[] {
    const shape = this.tree.shape;
    shape.locate(local, _rc);
    shape.point(_rc.root, _rc.u, _rc.v, _p);
    shape.tangents(_rc.root, _rc.u, _rc.v, _e1, _e2);
    const step = this.tree.nodeArc(this.level) * 0.9;
    const out = this.hood;
    let count = 0;
    for (let j = -1; j <= 1; j++) {
      for (let i = -1; i <= 1; i++) {
        _q.copy(_p)
          .addScaledVector(_e1, i * step)
          .addScaledVector(_e2, j * step);
        const n = this.nodeUnder(_q);
        let seen = false;
        for (let k = 0; k < count; k++) seen ||= out[k] === n;
        if (!seen) out[count++] = n;
      }
    }
    out.length = count;
    return out;
  }

  /** Drop the ground's collision (its tiles are gone); the mouth collars stay. */
  reset(): void {
    if (this.mesh) this.physics.removeStaticMesh(this.mesh);
    this.mesh = null;
    this.centerKey = -1;
    this.centerNode = null;
    this.builtCount = 0;
    this.wanted = [];
  }

  /**
   * Ask for the collision tiles again. update() runs at the physics rate, but the tree
   * forgets requests not repeated since the previous rendered frame, so the scene also
   * calls this every frame.
   */
  renew(): void {
    for (const n of this.wanted) this.tree.request(n, 5000);
  }

  update(player: THREE.Vector3): void {
    _pl.subVectors(player, this.hollowCenter);
    let center = this.nodeUnder(_pl);
    if (center.key !== this.centerKey && this.centerNode && this.holds(this.centerNode))
      center = this.centerNode;
    const nodes = this.neighbourhood(_pl);
    this.wanted = nodes;
    this.renew();
    let ready = 0;
    for (const n of nodes) if (n.heights) ready++;
    if (center.key === this.centerKey && ready <= this.builtCount) return;
    if (!center.heights) return;
    this.centerKey = center.key;
    this.centerNode = center;
    this.builtCount = ready;
    this.rebuild(
      center,
      nodes.filter((n) => n.heights),
    );
  }

  private rebuild(center: TNode, nodes: TNode[]): void {
    const shape = this.tree.shape;
    const anchor = center.point.clone().add(this.hollowCenter);
    const G = 32;
    const vertsPer = (G + 1) * (G + 1);
    const verts = new Float32Array(nodes.length * vertsPer * 3);
    const idx = new Uint32Array(nodes.length * G * G * 6);
    const blocked = new Uint8Array(nodes.length * vertsPer);
    let vi = 0;
    let ii = 0;
    for (let t = 0; t < nodes.length; t++) {
      const n = nodes[t];
      const size = 2 / 2 ** n.level;
      const u0 = -1 + n.x * size;
      const v0 = -1 + n.y * size;
      const heights = n.heights!;
      const baseV = t * vertsPer;
      for (let j = 0; j <= G; j++) {
        for (let i = 0; i <= G; i++) {
          const u = u0 + (i / G) * size;
          const v = v0 + (j / G) * size;
          const h = Math.max(0, heights[2 * j * TILE_RES + 2 * i]);
          // The ground: the reference surface lifted by the height, into the void.
          shape.point(n.face, u, v, _local).addScaledVector(shape.normal(n.face, u, v, _n), -h);
          if (this.mouths.length > 0) {
            for (const m of this.mouths) {
              if (mouthAxisDistance(m, _local) < m.radius + HOLE_MARGIN) blocked[baseV + j * (G + 1) + i] = 1;
            }
          }
          verts[vi++] = this.hollowCenter.x + _local.x - anchor.x;
          verts[vi++] = this.hollowCenter.y + _local.y - anchor.y;
          verts[vi++] = this.hollowCenter.z + _local.z - anchor.z;
        }
      }
      for (let j = 0; j < G; j++) {
        for (let i = 0; i < G; i++) {
          const a = baseV + j * (G + 1) + i;
          const b = a + 1;
          const c = a + G + 1;
          const d = c + 1;
          if (blocked[a] || blocked[b] || blocked[c] || blocked[d]) continue;
          idx[ii++] = a;
          idx[ii++] = c;
          idx[ii++] = b;
          idx[ii++] = b;
          idx[ii++] = c;
          idx[ii++] = d;
        }
      }
    }
    if (this.mesh) this.physics.removeStaticMesh(this.mesh);
    this.mesh = this.physics.addStaticMesh(anchor, verts, idx.subarray(0, ii));
    this.physics.wakeAll();
  }

  /**
   * True once the tiles around `player` all have collision: the one underfoot and its
   * neighbours, so a player who slides a little at first doesn't slip off the edge.
   */
  readyAt(player: THREE.Vector3): boolean {
    _pl.subVectors(player, this.hollowCenter);
    const under = this.nodeUnder(_pl);
    const c = this.centerNode;
    return c !== null && (c.key === under.key || this.holds(c)) && this.builtCount >= this.wanted.length;
  }
}
